import React, { useEffect } from 'react';
import {
    Box,
    Grid,
    TextField,
    Typography,
    styled,
    InputAdornment,
} from '@mui/material';
import Flex from 'ui/Flex/Flex';
import { StyPaper } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/PayConstants';
import {
    HrSimulation,
    SimulationDateString,
} from 'routes/HumanResourcesModule/Simulation/helpers/types';
import { parseNumericInput } from 'helpers/UIHelpers';
import NumberFormatCustom from 'components/NumberFormat/NumberFormatCustom';
import {
    OperationType,
    ValueType,
} from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/components/FreeHypothesis/helpers/types';
import { usePostIFSEInformation } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/components/FreeHypothesis/helpers/hooks/ifseInformation';
import ToggleHypothesisConditions from './HypothesisGroupButton';
import FilteredAgentNumber from './informationDisplay/FilteredAgentsNumber';
import HypothesisResume from './informationDisplay/HypothesisResume';
import ApplicationDateInput from './informationSelect/ApplicationDateInput';
import { useTranslation } from 'react-i18next';

const StyBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
}));

type Props = {
    hypothesisId?: number;
    applicationDate: SimulationDateString;
    hrSimulation: HrSimulation;
    cadreEmploi: string;
    category: string;
    filiere: string;
    job: string[];
    agentsNumber: number;
    setAgentsNumber: (agentsNumber: number) => void;
    maxValue: number | undefined;
    minValue: number | undefined;
    newValue: number | undefined;
    operationType: OperationType;
    setApplicationDate: (value: SimulationDateString) => void;
    setNewValue: (value: number | undefined) => void;
    setOperationType: (value: OperationType) => void;
    setValueType: (value: ValueType) => void;
    valueType: ValueType;
};
const ApplyConditionsStep = ({
    hypothesisId,
    applicationDate,
    agentsNumber,
    setAgentsNumber,
    hrSimulation,
    cadreEmploi,
    category,
    filiere,
    job,
    maxValue,
    minValue,
    newValue,
    operationType,
    setApplicationDate,
    setNewValue,
    setOperationType,
    setValueType,
    valueType,
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const onChangeNewValue = (event: React.ChangeEvent<HTMLInputElement>) => {
        const v = parseNumericInput(event.target.value);
        setNewValue(v === '' ? undefined : v);
    };

    const {
        data: retrieveAgentsNumbersResponse,
        isLoading: isAgentsNumberLoading,
    } = usePostIFSEInformation({
        filters: {
            category,
            filiere,
            cadreEmploi,
            jobLabel: job,
            lowerThan: maxValue,
            greaterThan: minValue,
        },
        simulationId: hrSimulation.id,
        hypothesisId: hypothesisId,
    });

    useEffect(() => {
        if (operationType === OperationType.FIX) {
            setValueType(ValueType.AMOUNT);
        }
    }, [operationType]);

    useEffect(() => {
        if (retrieveAgentsNumbersResponse)
            setAgentsNumber(
                retrieveAgentsNumbersResponse.uniqueAgentSoftwareIds.length,
            );
    }, [retrieveAgentsNumbersResponse]);

    return (
        <StyBox sx={{ minWidth: '10rem' }}>
            <Flex flexDirection="column" justifyContent="space-around">
                <FilteredAgentNumber
                    isLoading={isAgentsNumberLoading}
                    agentsNumber={agentsNumber}
                    minValue={minValue ?? 0}
                    maxValue={maxValue ?? 0}
                />

                <HypothesisResume
                    category={category}
                    filiere={filiere}
                    cadreEmploi={cadreEmploi}
                    job={job}
                />
            </Flex>
            <StyPaper sx={{ padding: '12px', width: '100%' }}>
                <Typography
                    variant="h4"
                    sx={{ paddingBottom: '12px', fontSize: '0.9rem' }}
                >
                    {t(
                        'humanResources.simulation.freeHypothesis.ifse.applyValues',
                    )}
                </Typography>
                <Grid container spacing={2}>
                    <Grid
                        item
                        xs={operationType !== OperationType.FIX ? 6 : 12}
                    >
                        <ToggleHypothesisConditions<OperationType>
                            optionSelected={operationType}
                            setOption={setOperationType}
                            option1={OperationType.INCREASE}
                            option2={OperationType.DECREASE}
                            option3={OperationType.FIX}
                        />
                    </Grid>
                    {operationType !== OperationType.FIX && (
                        <Grid item xs={6}>
                            <ToggleHypothesisConditions<ValueType>
                                optionSelected={valueType}
                                setOption={setValueType}
                                option1={ValueType.PERCENT}
                                option2={ValueType.AMOUNT}
                            />
                        </Grid>
                    )}
                    <Grid item xs={6}>
                        <TextField
                            sx={{
                                marginTop: '1em',
                                width: '100%',
                                backgroundColor: 'white',
                            }}
                            label={t(
                                'humanResources.simulation.freeHypothesis.ifse.newValue',
                            )}
                            required
                            value={newValue}
                            onChange={onChangeNewValue}
                            InputProps={{
                                inputComponent: NumberFormatCustom,
                                startAdornment: (
                                    <InputAdornment position="start">
                                        {valueType === ValueType.PERCENT
                                            ? '%'
                                            : '€'}
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <ApplicationDateInput
                            hrSimulation={hrSimulation}
                            applicationDate={applicationDate}
                            setApplicationDate={setApplicationDate}
                        />
                    </Grid>
                </Grid>
            </StyPaper>
        </StyBox>
    );
};

export default ApplyConditionsStep;
